import React, { useState, useRef, useEffect } from "react";
import { AuthLock } from "asset/icons";
import Loader from "component/loader";
import ForgotPinModal from "./ForgotPinModal";
import axios from "axios";
import EnterResetCodeModal from "./EnterResetCodeModal";
import SetNewPinModal from "./SetNewPinModal";

const PinModal = ({
    isOpen,
    onClose,
    onConfirm,
    isInvoice,
    goBack,
    header = "Enter payment PIN",
    pinErrorMessage = "",
    onClearExternalError,
}) => {
    const [pin, setPin] = useState(["", "", "", ""]);
    const inputRefs = useRef([]);
    const [loading, setLoading] = useState(false);
    const [pinError, setPinError] = useState("");
    const [forgotError, setForgotError] = useState("");
    const [resetError, setResetError] = useState("");
    const [updateError, setUpdateError] = useState("");
    const [showForgotPin, setShowForgotPin] = useState(false);
    const [showResetCodeModal, setShowResetCodeModal] = useState(false);
    const [showSetNewPinModal, setShowSetNewPinModal] = useState(false);
    const [forgotPinLoading, setForgotPinLoading] = useState(false);
    const [resetCodeLoading, setResetCodeLoading] = useState(false);
    const [newPinLoading, setNewPinLoading] = useState(false);
    const [confirmedCode, setConfirmedCode] = useState("");
    const shownError = pinError || pinErrorMessage;
    const hasPinError = Boolean(shownError);

    useEffect(() => {
        if (!isOpen) return;
        setPin(["", "", "", ""]);
        setLoading(false);
        setPinError("");
        setForgotError("");
        setResetError("");
        setUpdateError("");
        setShowForgotPin(false);
        setShowResetCodeModal(false);
        setShowSetNewPinModal(false);
        setForgotPinLoading(false);
        setResetCodeLoading(false);
        setNewPinLoading(false);
        setConfirmedCode("");
        onClearExternalError?.();
        setTimeout(() => inputRefs.current[0]?.focus(), 0);
    }, [isOpen, onClearExternalError]);

    const handleChange = (value, index) => {
        if (/^\d?$/.test(value)) {
            const next = [...pin];
            next[index] = value;
            setPin(next);
            if (hasPinError && pinError) setPinError("");
            if (value && index < 3) inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            e.preventDefault();
            const next = [...pin];
            if (pin[index]) {
                next[index] = "";
                setPin(next);
            } else if (index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
            if (hasPinError && pinError) setPinError("");
        } else if (e.key === "ArrowLeft" && index > 0) {
            inputRefs.current[index - 1]?.focus();
        } else if (e.key === "ArrowRight" && index < pin.length - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleConfirm = async () => {
        const joined = pin.join("");
        if (joined.length !== 4 || loading) return;

        setLoading(true);
        try {
            await onConfirm(joined);
            setPinError("");
            setPin(["", "", "", ""]);
        } catch (e) {
            const apiMsg =
                e?.response?.data?.message ||
                e?.response?.data?.error ||
                e?.message ||
                "Something went wrong. Please try again.";
            setPinError(String(apiMsg));
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        setPin(["", "", "", ""]);
        setPinError("");
        setForgotError("");
        setResetError("");
        setUpdateError("");
        setShowForgotPin(false);
        setShowResetCodeModal(false);
        setShowSetNewPinModal(false);
        setForgotPinLoading(false);
        setResetCodeLoading(false);
        setNewPinLoading(false);
        setConfirmedCode("");
        onClearExternalError?.();
        onClose?.();
    };

    const handleForgotPinContinue = async (password) => {
        setForgotPinLoading(true);
        try {
            await axios.post(
                "/v1/users/send-request-update-pin",
                new URLSearchParams({ password })
            );
            setShowForgotPin(false);
            setShowResetCodeModal(true);
            setForgotError("");
        } catch (err) {
            setForgotError(err.response?.data?.message || "Failed to request PIN reset.");
        } finally {
            setForgotPinLoading(false);
        }
    };

    const handlePinResetContinue = async (code) => {
        setResetCodeLoading(true);
        try {
            await axios.post("/v1/users/confirm-pin-reset", new URLSearchParams({ code }));
            setConfirmedCode(code);
            setShowResetCodeModal(false);
            setShowSetNewPinModal(true);
            setResetError("");
        } catch (err) {
            setResetError(err.response?.data?.message || "Failed to confirm PIN reset.");
        } finally {
            setResetCodeLoading(false);
        }
    };

    const handleSetNewPin = async (newPin) => {
        setNewPinLoading(true);
        try {
            await axios.post(
                "/v1/users/update-pin",
                new URLSearchParams({ pin: newPin, code: confirmedCode })
            );
            setShowSetNewPinModal(false);
            setUpdateError("");
        } catch (err) {
            setUpdateError(err.response?.data?.message || "Failed to update PIN.");
        } finally {
            setNewPinLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 z-[500] bg-black/40 flex items-center justify-center">
                <div className="relative w-full max-w-lg bg-white rounded-2xl">
                    <div className="absolute p-3 right-1">
                        <button onClick={handleClose} className="text-4xl" aria-label="Close">
                            &times;
                        </button>
                    </div>

                    <div className="p-6">
                        <div className="text-center">
                            <div className="flex items-center justify-center">
                                <AuthLock />
                            </div>

                            <h2 className="text-xl font-semibold">{header}</h2>

                            <div className="flex justify-center gap-4 mt-6">
                                {pin.map((digit, index) => (
                                    <input
                                        key={index}
                                        type="password"
                                        inputMode="numeric"
                                        maxLength={1}
                                        value={digit}
                                        onChange={(e) => handleChange(e.target.value, index)}
                                        onKeyDown={(e) => handleKeyDown(e, index)}
                                        ref={(el) => (inputRefs.current[index] = el)}
                                        className={`w-14 h-14 !shadow-none text-center text-2xl rounded-lg ${
                                            hasPinError
                                                ? "bg-[#FEE2E2] text-red-600"
                                                : "bg-[#EBEAF7]"
                                        }`}
                                    />
                                ))}
                            </div>

                            {hasPinError && (
                                <p className="text-sm text-red-600 text-center mt-2" role="alert">
                                    {shownError}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center justify-between p-3 mt-6 text-sm border-t">
                        <button
                            className="text-[#667085]"
                            onClick={() => {
                                setShowForgotPin(true);
                                setShowResetCodeModal(false);
                                setShowSetNewPinModal(false);
                                setPin(["", "", "", ""]);
                                setPinError("");
                                setForgotError("");
                                setResetError("");
                                setUpdateError("");
                                onClearExternalError?.();
                            }}
                        >
                            Forgot PIN?
                        </button>

                        <div className="flex items-center gap-2">
                            {isInvoice && (
                                <button
                                    onClick={() => goBack?.()}
                                    className="hover:opacity-90 mt-0 sm:!mt-0 flex items-center gap-2 text-gray-10 rounded-lg py-[10px] px-4 text-sm"
                                >
                                    Back
                                </button>
                            )}
                            <button
                                onClick={handleConfirm}
                                disabled={pin.some((d) => d === "") || loading}
                                className={`rounded-lg px-6 py-2 font-medium transition text-white ${
                                    pin.some((d) => d === "") || loading
                                        ? "bg-[#E9D7FE] text-[#B3A8DD] cursor-not-allowed"
                                        : "bg-[#6F00FF] hover:bg-[#5A00D6]"
                                }`}
                            >
                                {loading ? <Loader /> : "Confirm PIN"}
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <ForgotPinModal
                isOpen={showForgotPin}
                onClose={() => setShowForgotPin(false)}
                onBack={() => setShowForgotPin(false)}
                onContinue={handleForgotPinContinue}
                errorMessage={forgotError}
                loading={forgotPinLoading}
            />

            <EnterResetCodeModal
                isOpen={showResetCodeModal}
                onClose={() => setShowResetCodeModal(false)}
                onBack={() => {
                    setShowResetCodeModal(false);
                    setShowForgotPin(true);
                }}
                loading={resetCodeLoading}
                onContinue={handlePinResetContinue}
                errorMessage={resetError}
            />

            <SetNewPinModal
                isOpen={showSetNewPinModal}
                onClose={() => setShowSetNewPinModal(false)}
                onBack={() => {
                    setShowSetNewPinModal(false);
                    setShowResetCodeModal(true);
                }}
                onSubmit={handleSetNewPin}
                loading={newPinLoading}
                errorMessage={updateError}
            />
        </>
    );
};

export default PinModal;
